import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Button } from './Button';
import { Dialog } from './Dialog';
import { Input, Textarea } from './Input';
import { DUR, EASE_OUT } from '../motion';

const CONTACT_EMAIL = 'founders@agents2agents.ai';

/* FormSubmit relays the form to CONTACT_EMAIL with no account or API key. The
   address must be activated once: the first submission triggers a confirmation
   email to CONTACT_EMAIL, and submissions relay from then on. */
const ENDPOINT = `https://formsubmit.co/ajax/${CONTACT_EMAIL}`;

export function ContactDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
    const [sent, setSent] = React.useState(false);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState('');

    React.useEffect(() => {
        if (!open) return;
        setSent(false);
        setBusy(false);
        setError('');
    }, [open]);

    const submit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError('');
        setBusy(true);
        const data = new FormData(e.currentTarget);
        const company = String(data.get('company') || '').trim();
        const name = String(data.get('name') || '').trim();
        data.append('_subject', `Pilot request: ${company || name}`);
        data.append('_captcha', 'false');
        data.append('_template', 'table');

        try {
            const res = await fetch(ENDPOINT, {
                method: 'POST',
                body: data,
                headers: { Accept: 'application/json' },
            });
            if (!res.ok) throw new Error(String(res.status));
            setSent(true);
        } catch {
            setError(`Something went wrong. Email ${CONTACT_EMAIL} and we will pick it up there.`);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title="Give us a robot and a task"
            labelledBy="contact-dialog-title"
            width={520}
            description={
                sent
                    ? undefined
                    : `Tell us what you are deploying and where it is stuck. We reply from ${CONTACT_EMAIL}.`
            }
        >
            {/* The form and the confirmation cross-fade in place: same card, same
                scrim, no second dialog flashing past. */}
            <AnimatePresence mode="wait" initial={false}>
                {sent ? (
                    <motion.div
                        key="sent"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: DUR.base, ease: EASE_OUT }}
                    >
                        <p className="ds-dialog__desc ds-dialog__desc--confirm">
                            Thanks. We reply from <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
                        </p>
                        <div className="ds-dialog__foot">
                            <Button variant="ghost" size="md" onClick={onClose}>
                                Close
                            </Button>
                        </div>
                    </motion.div>
                ) : (
                    <motion.div
                        key="form"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: DUR.base, ease: EASE_OUT }}
                    >
                        <form className="ds-contact" onSubmit={submit}>
                            <Input label="Name" name="name" autoComplete="name" required />
                            <Input label="Email" name="email" type="email" autoComplete="email" required />
                            <Input label="Company (optional)" name="company" autoComplete="organization" />
                            <Textarea label="Your robot and task" name="message" rows={4} required />
                            {error && <p className="ds-contact__error">{error}</p>}
                            <div className="ds-dialog__foot">
                                <Button type="submit" size="md" disabled={busy}>
                                    {busy ? 'Sending\u2026' : 'Send'}
                                </Button>
                                <Button variant="ghost" size="md" onClick={onClose}>
                                    Cancel
                                </Button>
                            </div>
                        </form>
                    </motion.div>
                )}
            </AnimatePresence>
        </Dialog>
    );
}
